/**
 * PORTED — RO-11-05 / #44590.
 * Origin: `excellent/app/_chrome/rail-chrome-client.tsx`.
 *
 * Changes:
 *   - Next server actions (`@/workspaces/actions`) → Hono contracts via apiCall
 *     (`otherActions.workspaceSwitch` / `workspaceCreate`). On success, reload
 *     so the shell re-seeds against the new workspace cookie (mirrors
 *     revalidatePath("/", "layout") on the Next side).
 *   - Inline `toTitleCase` (pure; origin imported from @/projects/utils).
 *
 * Copied into client/shell/chrome/ — never import from app/ (PRD D6).
 */

import { useState, useTransition } from "react";
import {
  Building2,
  ChevronDown,
  Plus,
} from "@excellent/ui/icons";
import {
  cn,
  DropdownMenu,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  Button,
  type ContextMenuItem,
} from "@excellent/ui";
import { apiCall } from "@excellent/ui/api";
import { otherActions } from "@excellent/ui/api/contracts/other-actions";

export interface WorkspaceOption {
  id: number;
  slug: string;
  name: string;
}

function toTitleCase(s: string): string {
  return s
    .replace(/[-_]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/**
 * The top of the left navigation rail: the workspace switcher (login-gated).
 * The project-scope switcher that used to sit here was promoted to the top nav,
 * next to the logo (see top-nav-client.tsx). The products launcher that once
 * sat here has been removed — products are reached via Settings → Advanced →
 * Products.
 *
 * Workspaces arrive via props from RailChrome (shell data / fallback empty).
 * This component owns only the open/pending UI state.
 */
export function RailChromeClient({
  workspaces,
  activeWorkspaceId,
}: {
  workspaces: WorkspaceOption[];
  activeWorkspaceId: number | null;
}) {
  // Login-gated: with no workspaces there is nothing to switch, so render
  // nothing at all (no reserved/bordered slot — the rail starts at the body).
  if (workspaces.length === 0) return null;
  return (
    <div className="flex min-h-12 shrink-0 flex-col justify-center border-b border-border/40 px-2">
      <WorkspaceSwitcher
        workspaces={workspaces}
        activeWorkspaceId={activeWorkspaceId}
      />
    </div>
  );
}

// Full-width rail row shared by the two dropdown triggers below. Styled to
// match the section/Home nav rows (muted by default, foreground on hover) so
// the switchers read as part of the same navigation, not a distinct chrome
// surface.
const RAIL_ROW =
  "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 type-body tracking-tight text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors outline-none focus:outline-none focus-visible:outline-none";

function WorkspaceSwitcher({
  workspaces,
  activeWorkspaceId,
}: {
  workspaces: WorkspaceOption[];
  activeWorkspaceId: number | null;
}) {
  const [pending, startTransition] = useTransition();
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const active = workspaces.find((w) => w.id === activeWorkspaceId);
  const label = active ? toTitleCase(active.name) : "Workspace";

  // Server actions return a discriminated result; surface failures instead of
  // swallowing them. The only reason a user can trigger from this UI is an
  // empty name → invalid-name.
  const reasonCopy: Record<string, string> = {
    "invalid-name": "Enter a workspace name.",
    "not-a-member": "You don't have access to that workspace.",
    unauthenticated: "Sign in to manage workspaces.",
  };

  function submitCreate() {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError(reasonCopy["invalid-name"] ?? "Enter a workspace name.");
      return;
    }
    setError(null);
    startTransition(async () => {
      try {
        const res = await apiCall(otherActions.workspaceCreate, {
          body: { name: trimmed },
        });
        if (res.ok) {
          setName("");
          setCreateOpen(false);
          // Cookie was set by the response; reload to re-seed shell data.
          window.location.reload();
        } else {
          setError(reasonCopy[res.reason] ?? "Couldn't create the workspace.");
        }
      } catch {
        setError("Couldn't create the workspace.");
      }
    });
  }

  const items: ContextMenuItem[] = [
    { type: "label", label: "Workspace" },
    ...workspaces.map((w) => ({
      label: toTitleCase(w.name),
      selected: w.id === activeWorkspaceId,
      onSelect: () =>
        startTransition(async () => {
          if (w.id === activeWorkspaceId) return;
          try {
            const res = await apiCall(otherActions.workspaceSwitch, {
              body: { slug: w.slug },
            });
            if (res.ok) window.location.reload();
          } catch {
            /* surface nothing — switcher stays on the prior workspace */
          }
        }),
    })),
    { type: "separator" as const },
    {
      label: "Create workspace…",
      icon: <Plus strokeWidth={1.75} className="h-3.5 w-3.5" />,
      onSelect: () => {
        setError(null);
        setName("");
        setCreateOpen(true);
      },
    },
  ];

  return (
    <>
      <DropdownMenu items={items} side="right" align="start" widthPx={208}>
        <button
          type="button"
          disabled={pending}
          aria-label={`Workspace: ${label}`}
          className={cn(RAIL_ROW, pending && "opacity-60")}
        >
          <Building2
            strokeWidth={1.5}
            className="h-[15px] w-[15px] shrink-0 text-muted-foreground"
          />
          <span className="truncate">{label}</span>
          <ChevronDown
            strokeWidth={1.75}
            className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground"
          />
        </button>
      </DropdownMenu>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Create workspace</DialogTitle>
            <DialogDescription>
              A workspace holds its own projects, tasks, and data. You can switch
              between workspaces anytime.
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            value={name}
            placeholder="Workspace name"
            aria-label="Workspace name"
            disabled={pending}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitCreate();
              }
            }}
          />
          {error && <p className="type-body text-destructive">{error}</p>}
          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              disabled={pending}
              onClick={() => setCreateOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={pending || name.trim().length === 0}
              onClick={submitCreate}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
